import { useCallback, useMemo, useRef } from 'react'
import { useFireSessionContext } from '@chilipiper/fire-session'
import {
  QueryObserver,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiClient } from '@chilipiper/api-client/src/context'
import Workspace, {
  BulkInviteRequest,
  BulkInviteResponse,
  BulkUpdateUserLicensesRequestUpdate,
  BulkUpdateUserRolesRequest,
  IsAdmin,
  Role,
  UpdateUserWorkspacesRequest,
  UserAggregate,
} from '@chilipiper/api-client/src/workspace'
import chunk from 'lodash/chunk'
import invariant from 'tiny-invariant'
import DataLoader from 'dataloader'
import { licensesQueryKey } from './licences'
import { queryKeyFactory } from './teams'

export const useValidateEmails = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (emails: string[]) => workspace.validateEmails(tenantId, { emails }),
  })
  return { mutate, isLoading }
}

export const usersTableQueryKey = ['users-table']
export const PAGE_SIZE = 50
/** @deprecated TODO use local hooks */
export const useUsersTable = (
  query: Omit<Parameters<typeof Workspace.prototype.findAll>[1], 'page' | 'pageSize'>,
  options?: { enabled?: boolean }
) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const serializedQuery = JSON.stringify(query)
  const queryKey = useMemo(() => [...usersTableQueryKey, serializedQuery], [serializedQuery])

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam: page }) => {
      const data = await workspace.findAll(tenantId, {
        page,
        pageSize: PAGE_SIZE,
        ...query,
      })
      return {
        ...data,
        results: data.results ?? [],
      }
    },
    initialPageParam: 0,
    getNextPageParam: data => {
      return data.page * data.pageSize < data.total ? data.page + 1 : undefined
    },
    enabled: options?.enabled,
  })

  const total = data?.pages?.[0]?.total

  const mergedData = useMemo(() => {
    const allData: UserAggregate[] = []
    for (const page of data?.pages ?? []) {
      allData.push(...page.results)
    }
    return allData
  }, [data?.pages])

  const optimisticUpdate = useCallback(
    (updater: (users: UserAggregate[]) => UserAggregate[]) => {
      type Data = typeof data
      queryClient.setQueryData<Data>(queryKey, data => {
        const allData: UserAggregate[] = []
        for (const page of data?.pages ?? []) {
          allData.push(...page.results)
        }
        const updatedData = updater(allData)
        const prevTotal = data?.pages?.[0].total ?? 0
        const total = prevTotal + (updatedData.length - allData.length)
        const pages: NonNullable<Data>['pages'] = chunk(updatedData, PAGE_SIZE).map(
          (results, index) => ({
            results,
            page: data?.pages[index]?.page ?? index,
            pageSize: results.length,
            total,
          })
        )
        if (pages.length === 0) {
          return undefined
        }
        return { pages, pageParams: (data?.pageParams ?? []).slice(0, pages.length) }
      })
    },
    [queryClient, queryKey]
  )

  const nextPageFetchPending = useRef(false)

  return {
    optimisticUpdate,
    usersToDisplay: mergedData,
    fetchMore: () => {
      if (isFetchingNextPage || isLoading) {
        return
      }
      const isSomePageFetching = queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching'
      if (isSomePageFetching) {
        nextPageFetchPending.current = true
        const observer = new QueryObserver(queryClient, { queryKey })
        const unsubscribe = observer.subscribe(({ isFetching }) => {
          if (!isFetching && nextPageFetchPending.current) {
            nextPageFetchPending.current = false
            fetchNextPage()
            unsubscribe()
          }
        })
      } else {
        fetchNextPage()
      }
    },
    isLoading,
    total,
  }
}

export const useBulkSendInvite = ({
  onSuccess,
  onError,
}: {
  onError?: (error: unknown) => void
  onSuccess?: () => void
}) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (userIds: string[]) =>
      workspace.bulkSendInviteNotifications(tenantId, { users: userIds }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: usersTableQueryKey,
      })
    },
    onSuccess,
    onError,
  })
  return { mutate }
}

export const useBulkAddUsers = ({
  onError,
  onSuccess,
}: {
  onError?: (error: unknown) => void
  onSuccess?: (data: BulkInviteResponse) => void
}) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate, mutateAsync } = useMutation({
    mutationFn: (data: BulkInviteRequest) => workspace.bulkInvite(tenantId, data),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: usersTableQueryKey,
      })
    },
    onError,
    onSuccess,
  })
  return { mutate, mutateAsync }
}

export const usersQueryKeys = {
  allUsers: (tenantId: string) => ['users', tenantId],
  findUsers: (tenantId: string, options: Parameters<typeof Workspace.prototype.findAll>[1]) => [
    ...usersQueryKeys.allUsers(tenantId),
    options,
  ],
  findUsersByEmail: (tenantId: string, users: string[]) => [
    ...usersQueryKeys.allUsers(tenantId),
    users,
  ],
  user: (tenantId: string, userId: string) => [...usersQueryKeys.allUsers(tenantId), userId],
}
export const useUsers = (
  options?: Parameters<typeof Workspace.prototype.findAll>[1],
  queryOptions?: { enabled?: boolean }
) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const { data, isLoading, fetchStatus } = useQuery({
    queryKey: usersQueryKeys.findUsers(tenantId, options),
    queryFn: () => workspace.findAll(tenantId, options),
    ...queryOptions,
  })

  return {
    data,
    isLoading: isLoading && fetchStatus !== 'idle',
  }
}

export const usePaginatedUsers = (options?: Parameters<typeof Workspace.prototype.findAll>[1]) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: usersQueryKeys.findUsers(tenantId, options),
    queryFn: ({ pageParam }) => workspace.findAll(tenantId, { ...options, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: data => {
      if (data && data.total > data.page * data.pageSize + (data.results ?? []).length) {
        return data.page + 1
      }
    },
  })

  const mergedData = useMemo(() => {
    if (!data) {
      return undefined
    }
    const allUsers = data.pages.flatMap(page => page.results ?? [])
    const lastPage = data.pages.at(-1)
    return {
      results: allUsers,
      total: lastPage?.total ?? allUsers.length,
    }
  }, [data])
  return { data: mergedData, fetchNextPage, isFetchingNextPage, isLoading }
}

export const getUserQueryOptions = ({
  workspace,
  tenantId,
  userId,
}: {
  tenantId: string
  userId: string
  workspace: Workspace
}) => ({
  queryKey: usersQueryKeys.user(tenantId, userId),
  queryFn: () => workspace.getUserAggregate(tenantId, userId),
})

export const useUser = (userId: string, { enabled }: { enabled?: boolean } = {}) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const { queryKey, queryFn } = getUserQueryOptions({ workspace, tenantId, userId })
  const { data, isLoading, isError, isInitialLoading } = useQuery({
    queryKey,
    queryFn,
    retryOnMount: false,
    enabled,
  })

  return {
    data,
    isLoading,
    isInitialLoading,
    isError,
  }
}

export const usersByIdsQueryKey = (tenantId: string) => ['users', tenantId]

export const useFetchUsersByIds = <T extends string | string[]>(
  ids: T
): {
  data: (T extends string[] ? UserAggregate[] : UserAggregate) | undefined
  isError: boolean
  isLoading: boolean
} => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: [...usersByIdsQueryKey(tenantId), ids],
    queryFn: () => {
      return (
        Array.isArray(ids)
          ? workspace.findByIds(tenantId, {
              id: ids,
            })
          : workspace.getUserAggregate(tenantId, ids)
      ) as Promise<T extends string[] ? UserAggregate[] : UserAggregate>
    },
    enabled: Boolean(Array.isArray(ids) ? ids.length : ids),
  })

  return { data, isLoading: Boolean(Array.isArray(ids) ? ids.length : ids) && isLoading, isError }
}

export const useFetchAllUsersIds = (query: {
  'chili-cal-org'?: boolean
  distro?: boolean
  'is-invited'?: boolean
  role?: IsAdmin
  search?: string
  workspace?: string
}) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return async () => {
    const users = await workspace.findAll(tenantId, {
      ...query,
      pageSize: 10000000,
    })

    return users.results?.map(({ id }) => id) ?? []
  }
}

export const useFetchAllUsers = (query: {
  'chili-cal-org'?: boolean
  distro?: boolean
  concierge?: boolean
  chilical?: boolean
  'is-invited'?: boolean
  role?: IsAdmin
  search?: string
  workspace?: string
}) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: ['workspace', tenantId, 'fetch-all-users'],
    queryFn: () =>
      workspace
        .findAll(tenantId, {
          ...query,
          pageSize: 10000000,
        })
        .then(result => result.results),
    staleTime: Infinity,
  })
}

export const useUpdateUserWorkspaces = () => {
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()
  const { tenantId } = useFireSessionContext()

  return useMutation({
    mutationFn: (updates: UpdateUserWorkspacesRequest | UpdateUserWorkspacesRequest[]) =>
      workspace.updateWorkspaces(tenantId, {
        updates: Array.isArray(updates) ? updates : [updates],
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: usersTableQueryKey,
      })
    },
  })
}

export const useUpdateLicense = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const {
    mutate,
    mutateAsync,
    isPending: isLoading,
  } = useMutation({
    mutationFn: (
      updates: BulkUpdateUserLicensesRequestUpdate | BulkUpdateUserLicensesRequestUpdate[]
    ) =>
      Array.isArray(updates)
        ? workspace.bulkUpdateLicenses(tenantId, { updates })
        : workspace.updateLicenses(tenantId, updates.userId, {
            licenses: updates.licenses,
          }),
    onSettled: (_result, _error, updates) => {
      void queryClient.invalidateQueries({ queryKey: licensesQueryKey(tenantId) })
      void queryClient.invalidateQueries({
        queryKey: usersTableQueryKey,
      })
      void queryClient.invalidateQueries({ queryKey: usersQueryKeys.allUsers(tenantId) })

      if (Array.isArray(updates)) {
        updates.forEach(update => {
          void queryClient.invalidateQueries({
            queryKey: queryKeyFactory.queryTeams(tenantId, { member: [update.userId] }),
          })
        })
      } else {
        void queryClient.invalidateQueries({
          queryKey: queryKeyFactory.queryTeams(tenantId, { member: [updates.userId] }),
        })
      }
    },
  })
  return { mutate, mutateAsync, isLoading }
}

const loadProfilePicture = async (
  workspace: Workspace,
  tenantId: string,
  userId: string
): Promise<string | null> => {
  try {
    const data = await workspace.getPictureGuest(tenantId, userId)
    return data.url
  } catch (e) {
    if (e instanceof Response && e.status === 404) {
      return null
    }
    throw e
  }
}

const useProfilePicturesLoader = (tenantId: string) => {
  const { workspace } = useApiClient()
  return new DataLoader<string, string | null>(
    async keys => {
      const pictures = await workspace.getPicturesGuest(tenantId, { id: keys as string[] })
      return keys.map(key => pictures[key] || null)
    },
    { cache: false }
  )
}

export const useProfilePictureUrls = (tenantId: string, userIds: string[]) => {
  const loader = useProfilePicturesLoader(tenantId)

  const queries = useQueries({
    queries: userIds.map(userId =>
      queryOptions({
        queryKey: ['profile-picture', tenantId, userId],
        queryFn: () => {
          return loader.load(userId)
        },
        enabled: !!userId,
        staleTime: Infinity,
      })
    ),
  })

  return queries.map(query => ({ ...query, data: query.data ?? undefined }))
}

export const useProfilePictureUrl = (userId?: string) => {
  const { workspace } = useApiClient()
  const { tenantId } = useFireSessionContext()

  const { data, isLoading } = useQuery({
    queryKey: ['profile-picture', tenantId, userId, workspace],
    queryFn: (): Promise<string | null> => {
      invariant(userId, 'userId is required')
      return loadProfilePicture(workspace, tenantId, userId)
    },
    enabled: !!userId,
    staleTime: Infinity,
  })
  return { data: data ?? undefined, isLoading }
}

export const useFindByEmails = (emails: string[] = [], options?: { enabled?: boolean }) => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()

  return useQuery({
    queryKey: usersQueryKeys.findUsersByEmail(tenantId, emails),
    queryFn: async () => {
      if (!emails.length) {
        return []
      }
      return workspace.findUmtUsersByEmails(tenantId, { email: emails }).then(result =>
        result.map(user => ({
          ...user,
          connections: {
            salesforce: user.connections.salesforce === 'Connected',
            hubspot: user.connections.hubspot === 'Connected',
            google: user.connections.google === 'Connected',
            microsoft: user.connections.microsoft === 'Connected',
            zoom: user.connections.zoom === 'Connected',
            gong: user.connections.gong === 'Connected',
          },
        }))
      )
    },
    ...options,
  })
}

export const useUpdateUserRoles = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ roles, userId }: { roles: Role[]; userId: string }) =>
      workspace.updateUserRoles(tenantId, userId, {
        roles,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: usersTableQueryKey,
      })
    },
  })
}

export const useBulkUpdateUserRoles = () => {
  const { tenantId } = useFireSessionContext()
  const { workspace } = useApiClient()
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (request: Required<BulkUpdateUserRolesRequest>) =>
      workspace.bulkUpdateUserRoles(tenantId, request),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: usersTableQueryKey,
      })
    },
  })
  return { mutate }
}
